import { COMMISSION_TRACE_KEY, FINAL_PAYOUT_KEY } from "@/icm/constants";
import type {
  EnrichedIcmStatement,
  IcmLineItem,
  IcmPayoutBreakdownItem,
} from "@/icm/types";
import { OWN_COLUMN_KEYS } from "./statement-rule-table/cols";

export const groupLineItemsByRule = (items: IcmLineItem[]) =>
  items.reduce<Record<string, IcmLineItem[]>>((acc, item) => {
    acc[item.payout_rule_name] = [...(acc[item.payout_rule_name] ?? []), item];
    return acc;
  }, {});

export const getJobCount = (items: IcmLineItem[]) =>
  new Set(items.map((item) => item.job_id)).size;

export const getTotalPayout = (items: IcmLineItem[]) =>
  parseFloat(
    items
      .reduce((sum, item) => {
        const payout = Number(item.payout_amount);
        return sum + (Number.isNaN(payout) ? 0 : payout);
      }, 0)
      .toFixed(2),
  );

export const getTotalHours = (items: IcmLineItem[]) => {
  if (!items.length) return null;

  let hasHours = false;
  const totalHours = items.reduce((sum, item) => {
    const rawHours = item.calculation_trace.hours;
    if (
      rawHours === null ||
      rawHours === undefined ||
      (typeof rawHours === "string" && rawHours.trim() === "") ||
      (typeof rawHours !== "number" && typeof rawHours !== "string")
    ) {
      return sum;
    }

    const hours = Number(rawHours);
    if (!Number.isFinite(hours)) return sum;

    hasHours = true;
    return sum + hours;
  }, 0);

  return hasHours ? parseFloat(totalHours.toFixed(2)) : null;
};

export const buildStatementBreakdownItems = (
  statement?: Pick<EnrichedIcmStatement, "payout_breakdown_items">,
): IcmPayoutBreakdownItem[] =>
  (statement?.payout_breakdown_items ?? []).filter(
    (item) => item.key !== FINAL_PAYOUT_KEY,
  );

export const visibleTraceKeys = (items: IcmLineItem[]): string[] =>
  Array.from(
    new Set(items.flatMap((item) => Object.keys(item.calculation_trace))),
  ).filter((key) => {
    if (key.toLowerCase() === COMMISSION_TRACE_KEY) return false;
    if (OWN_COLUMN_KEYS.has(key)) return false;
    if (key.startsWith("is_")) return false;

    return items.some((item) => {
      const value = item.calculation_trace[key];
      return (
        value !== null && value !== undefined && value !== 0 && value !== "0"
      );
    });
  });
